import 'reflect-metadata';
import './main.css';

import { registerSW } from 'virtual:pwa-register';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from '@capacitor-community/safe-area';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import * as Sentry from '@sentry/react';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { Query, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { baseClient } from '@portal-clients/common';

import { App } from '@frontend/app';

import { initAnalytics } from './analytics';
import { getAuthToken, setAuthToken } from './auth';
import { LicenseConsumer } from './licenses';
import { I18nProvider } from './locales';
import { registerEarlyPushNotificationListener } from './notifications';
import { PortalPublicConfigurationProvider } from './portal-public-configuration';
import { KeyValueStore, VersionExpectationsProvider } from './utils';
import { ErrorView } from './views';

CapacitorUpdater.notifyAppReady().catch(console.error);
registerEarlyPushNotificationListener();

if (!Capacitor.isNativePlatform()) {
  const updateSW = registerSW({
    onNeedRefresh() {
      updateSW();
    },
  });
}

baseClient.setBaseUrl(APP_CONFIG.baseUrl, APP_CONFIG.baseHeaders);
baseClient.setStorageEngine({
  getToken: getAuthToken,
  setToken: setAuthToken,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 604800000, // 7 days
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: {
    getItem: async (key) => {
      return await KeyValueStore.get(key);
    },
    setItem: async (key, value) => {
      await KeyValueStore.set(key, value);
    },
    removeItem: async (key) => {
      await KeyValueStore.remove(key);
    },
  },
});

const posthogClient = initAnalytics();

Sentry.init({
  ...APP_CONFIG.sentry.initConfig,
  environment: APP_CONFIG.environment,
  ignoreErrors: [
    ...(APP_CONFIG.sentry.initConfig.ignoreErrors ?? []),
    // JWT expiry is expected — the app handles it by prompting re-login.
    /JWT token has expired/,
  ],
  integrations: [
    posthogClient.sentryIntegration({
      organization: APP_CONFIG.sentry.organization,
      projectId: APP_CONFIG.sentry.projectId,
      severityAllowList: ['error', 'info'],
    }),
  ],
});

Sentry.setTags({
  stack: APP_CONFIG.stack,
});

// Make sure that SafeArea is bundled in the app. There is no other endpoint we can really use,
// so our best bet is simply to call it with default options at the start of the app which is
// essentially a no-op since we always show system bars.
SafeArea.showSystemBars({}).catch(console.error);

createRoot(document.querySelector('#root')! as HTMLElement, {
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.error('Uncaught error', error, errorInfo.componentStack);
  }),
}).render(
  <StrictMode>
    <PostHogProvider client={posthogClient}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          buster: __APP_VERSION_HASH__,
          dehydrateOptions: {
            shouldDehydrateQuery: (query: Query) =>
              query.state.status === 'success' &&
              !query.queryKey.includes('verify-session'),
          },
        }}
      >
        <PortalPublicConfigurationProvider>
          <Sentry.ErrorBoundary
            fallback={<ErrorView />}
            onError={console.error}
          >
            <LicenseConsumer>
              <I18nProvider>
                <VersionExpectationsProvider>
                  <App />
                </VersionExpectationsProvider>
              </I18nProvider>
            </LicenseConsumer>
          </Sentry.ErrorBoundary>
        </PortalPublicConfigurationProvider>
      </PersistQueryClientProvider>
    </PostHogProvider>
  </StrictMode>,
);
